/* eslint-disable prettier/prettier */
'use client';

import { ToastContentProps } from 'react-toastify';

import Link from 'next/link';

import '@/styles/globals.v2.css';

import { useEffect, useMemo } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import NProgress from 'nprogress';

import closeIcon from '@/assets/icons/x-cart-close-icon.svg';
import KlaviyoComponent from '@/components/Klaviyo';
import ShoppingCartProvider, { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import { triggerCheckoutExperienceModal } from '@/components/V2/Cart/ChooseYourCheckoutExperience';
import CartItem from '@/components/V2/Popup/AddedToCart/CartItem';
import { CustomerTypes } from '@/helpers/constants/shopping';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { findEnrollmentPackItemsInCart } from '@/helpers/utils/enrollmentFlowTracker';
import { mapDataToAddedToCart } from '@/helpers/utils/klaviyo';
import { generateApproperiateURL, getCartUrlWithRecommendationParam } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { AddToCartToast } from '@/types/Cart';
import { Customer } from '@/types/Customer';

const ENROLLMENT_CHECKOUT_ROUTE = '/enrollment/enrolleeinfo';
const RECOMMENDATION_FLOW_ROUTE = 'petprofile/createrecommendationnew';

interface AddedToCartPopupProps extends Omit<ToastContentProps<unknown>, 'toastProps' | 'data'> {
  product: AddToCartToast;
  totalItemsInCart?: number;
}

const AddedToCartPopupComp: React.FC<AddedToCartPopupProps> = ({ product, closeToast, totalItemsInCart }) => {
  const { webalias } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { cart } = useShoppingCartContext();
  const isAuthenticated = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);
  // Get customer data from Redux store to check if user is already a petPro
  const { data: customerData } =
    useAppSelector(state => state.customerApi.queries[`getCustomer(undefined)`] as { data: { data: Customer } }) || {};
  const customer = customerData?.data;
  const isPetPro = customer?.customerTypeID === CustomerTypes.PET_PRO;
  const router = useRouter();
  const totalItems = totalItemsInCart ?? 0;
  const proceedToCheckoutUrl = totalItems > 0 ? '/checkout/preorder?v=' + Date.now() : '/products';
  const cartUrl = getCartUrlWithRecommendationParam(isAuthenticated);

  const _cart = useMemo(() => cart || [], [cart]);

  // Check if user has visited recommendation flow (from sessionStorage, URL param, or currently on recommendation route)
  const isFromPetRecommendations = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const hasVisitedRecommendationFlow = sessionStorage.getItem('hasVisitedRecommendationFlow') === 'true';
    const urlParams = new URLSearchParams(window.location.search);
    const isFromRecommendationParam = urlParams.get('isFromPetRecommendations') === 'true';
    // Also check if user is currently on the recommendation flow route
    const isOnRecommendationRoute = window.location.pathname.includes(RECOMMENDATION_FLOW_ROUTE);
    return hasVisitedRecommendationFlow || isFromRecommendationParam || isOnRecommendationRoute;
  }, []);

  // Check if cart has enrollment pack items
  const hasEnrollmentPackItems = useMemo(() => {
    if (!_cart || _cart.length === 0) return false;
    return findEnrollmentPackItemsInCart(_cart).length > 0;
  }, [_cart]);

  const handleCheckoutClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    // If cart is empty, redirect to products page
    if (totalItems === 0) {
      closeToast && closeToast();
      sessionStorage.removeItem('hasVisitedRecommendationFlow');
      router.push(generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias));
      return;
    }

    // If user came from recommendation flow AND is not already a petPro, trigger the checkout experience modal
    // petPros (customerTypeID === 3) should go directly to checkout without seeing the modal
    if (isFromPetRecommendations && !isPetPro) {
      NProgress.done();
      triggerCheckoutExperienceModal();
      closeToast && closeToast();
      return;
    }

    // If cart has enrollment pack items (and NOT from recommendation flow), go to enrollment checkout
    if (hasEnrollmentPackItems) {
      closeToast && closeToast();
      router.push(generateApproperiateURL(ENROLLMENT_CHECKOUT_ROUTE, isBackOffice ? 'shopping' : webalias));
      return;
    }

    // Default: go to regular checkout (original behavior)
    localStorage.removeItem('petProfile');
    closeToast && closeToast();
    router.push(generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias));
  };

  useEffect(() => {
    try {
      if (_cart && product) {
        const itemFromCart = _cart.find(x => x.name == product.name && x.price == product.price);

        if (itemFromCart && globalThis?.window?.dataLayer) {
          globalThis?.window?.dataLayer.push({ ecommerce: null });
          globalThis?.window?.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'USD',
              value: +(product.price ?? 0) * (product.quantity ?? 1) || 1,
              items: [
                {
                  item_id: itemFromCart.id,
                  item_name: itemFromCart.name,
                  index: 0,
                  item_category: itemFromCart.category,
                  price: itemFromCart.price,
                  quantity: itemFromCart.quantity,
                },
              ],
            },
          });
        }
      }
    } catch {
      console.log('Failed to log add_to_cart to gtm');
    }
  }, [product, _cart]);

  const klaviyoData = useMemo(() => {
    const getRecentlyAddedItem = () => {
      if (_cart && product) {
        const itemFromCart = _cart.find(x => x.name == product.name && x.price == product.price);
        if (itemFromCart) {
          return {
            quantity: product.quantity,
            isEZShip: itemFromCart.isEZShip,
            totalPrice: itemFromCart.sampleCredits,
            totalPawPoints: product.pawPoints,
            label: itemFromCart.label,
            isFoodItem: itemFromCart.isFoodItem,
            pawPoint: product.pawPoints ?? 0,
            sampleCredits: product.sampleCredits ?? 0,
            paymentType: product.paymentType,
            foodType: itemFromCart.foodType,
            id: itemFromCart.id,
            itemId: itemFromCart.itemId,
            name: itemFromCart.name,
            image: itemFromCart.image,
            price: itemFromCart.price,
            idealFor: itemFromCart.idealFor,
            category: itemFromCart.category,
            weight: itemFromCart.weight,
            isFreeShipping: itemFromCart.isFreeShipping,
            isOutOfStock: itemFromCart.isOutOfStock,
          };
        }
      }
      return null;
    };
    const recentItem = getRecentlyAddedItem();

    return mapDataToAddedToCart(_cart, recentItem, globalThis?.window?.location?.origin);
  }, [_cart, product]);

  return (
    <div className="v2-relative v2-flex sm:v2-gap-6 v2-gap-2">
      <KlaviyoComponent type={'events'} event={'Added to Cart'} data={klaviyoData} />

      <Image
        src={closeIcon}
        className="v2-absolute -v2-right-6 v2-top-3 -v2-translate-x-1/2 -v2-translate-y-1/2 v2-z-[999] v2-p-1 v2-cursor-pointer"
        onClick={closeToast}
        alt="close add to cart toast"
        height={36}
        width={36}
      />
      <CartItem product={product}>
        <CartItem.ImageDisplay />
      </CartItem>
      <div>
        <h3 className="v2-flex v2-items-center v2-text-gray-700 v2-text-sm sm:v2-text-base v2-font-semibold v2-pr-[30px]">
          <span>{product.name} Added to Cart!</span>
        </h3>
        <CartItem product={product}>
          <CartItem.ProductInfo />
        </CartItem>
        <div>
          <Link
            onClick={() => {
              closeToast && closeToast();
            }}
            href={generateApproperiateURL(cartUrl, isBackOffice ? 'shopping' : webalias)}
            className="v2-text-sm sm:v2-text-base v2-font-medium v2-text-gray-600"
          >
            View Cart ({totalItemsInCart})
          </Link>
          {/* When showing modal, use span to avoid triggering nextjs-toploader. Otherwise use anchor for normal navigation */}
          {/* petPros (customerTypeID === 3) should go directly to checkout without seeing the modal */}
          {isFromPetRecommendations && !isPetPro ? (
            <span
              onClick={() => {
                triggerCheckoutExperienceModal();
                closeToast && closeToast();
              }}
              className="v2-pl-4 v2-text-sm sm:v2-text-base v2-font-medium v2-text-primary-700 v2-cursor-pointer"
              id="proceed-to-checkout"
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  triggerCheckoutExperienceModal();
                  closeToast && closeToast();
                }
              }}
            >
              Checkout
            </span>
          ) : (
            <a
              onClick={handleCheckoutClick}
              href={generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias)}
              className="v2-pl-4 v2-text-sm sm:v2-text-base v2-font-medium v2-text-primary-700"
              id="proceed-to-checkout"
            >
              Checkout
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const AddedToCartPopup: React.FC<AddedToCartPopupProps> = props => {
  return (
    <ShoppingCartProvider>
      <AddedToCartPopupComp {...props} />
    </ShoppingCartProvider>
  );
};

export default AddedToCartPopup;
